import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { PHOTO_LABEL_CLASSES } from './photoControls';
import Spinner from './Spinner';

/**
 * Button — the one button (visual refresh, 2026-10-01).
 *
 * Every button is an 8px rounded rectangle (`rounded-lg`), the shape Airbnb and
 * Instagram use. `shape="pill"` exists for exactly one case: a button floating
 * over a map or a photo (e.g. "Browse list" on the search map). Chips, filters
 * and segmented controls are not buttons — they stay pills on their own.
 *
 * Props:
 * @param {'primary'|'secondary'|'ghost'|'danger'|'dangerOutline'|'photo'|'photoGlass'} variant
 *   primary       — the page's one main action (solid harbour blue)
 *   secondary     — every other action (white with a hairline border)
 *   ghost         — low-emphasis inline action (blue text, no frame)
 *   danger        — destructive confirm (solid crimson)
 *   dangerOutline — a destructive action offered beside others, before any
 *                   confirm step ("Stop sharing", "Remove from trip")
 *   photo         — the main action floating over a photo (white glass)
 *   photoGlass    — any other action over a photo (dark glass)
 * @param {'sm'|'md'|'lg'} size - sm 36px (dense rows), md 44px (default, the
 *   touch minimum), lg 48px (full-width form submits)
 * @param {'default'|'pill'} shape - pill ONLY when floating over a map/photo
 * @param {string} icon - Leading Icon name. With no children the button
 *   is an icon-only square (pass an aria-label)
 * @param {string} iconRight - Trailing Icon name
 * @param {boolean} loading - Shows a spinner, sets aria-busy and blocks clicks.
 *   Keep the label meaningful ("Saving…") — the spinner doesn't replace it.
 * @param {boolean} wrap - Let the label wrap onto more lines (height grows).
 *   Use when the label holds user text (a trip or place name); the default
 *   single line would push the page sideways on a long name.
 * @param {boolean} fullWidth
 * @param {string} to - Renders a router <Link> (keeps cmd-click/new-tab)
 * @param {string} href - Renders an <a> (external links: pass target/rel too)
 * Anything else (type, onClick, aria-*, data-*) passes through.
 */
const VARIANT_CLASSES = {
  primary:
    'bg-primary text-content-inverse hover:bg-primary-dark focus-visible:ring-primary-light',
  secondary:
    'bg-surface text-content-primary border border-border hover:bg-surface-secondary focus-visible:ring-primary-light',
  ghost:
    'text-primary hover:bg-primary-lighter focus-visible:ring-primary-light',
  danger:
    'bg-error text-content-inverse hover:bg-error-dark focus-visible:ring-error',
  dangerOutline:
    'bg-surface text-error-dark border border-error/50 hover:bg-error-light focus-visible:ring-error',
  // Over a photo (with shape="pill"): the main action as white glass, the
  // rest as the dark glass of PHOTO_LABEL_CLASSES — they let the photo through.
  // Their focus ring sits flush (FLUSH_RING_VARIANTS) in a colour that stands
  // off the pill: a white ring on white glass would only make it look wider.
  photo:
    'bg-white/85 text-primary-deep backdrop-blur-sm hover:bg-white focus-visible:ring-primary-deep',
  photoGlass:
    `${PHOTO_LABEL_CLASSES} focus-visible:ring-white`,
};

const FLUSH_RING_VARIANTS = new Set(['photo', 'photoGlass']);

const SIZE_CLASSES = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-base gap-2',
};

// Same sizes with min-h instead of h, written out in full so Tailwind's
// class scanner generates them (a class built at runtime never would be).
const WRAP_SIZE_CLASSES = {
  sm: 'min-h-9 px-3 text-sm gap-1.5',
  md: 'min-h-11 px-4 text-sm gap-2',
  lg: 'min-h-12 px-5 text-base gap-2',
};

// Icon-only (an `icon`, no label): a square at the same height. A "+" add
// button whose heading already names the thing (2026-10-02 add-button rule).
// Pass an aria-label: there is no visible text.
const ICON_ONLY_SIZE_CLASSES = { sm: 'h-9 w-9', md: 'h-11 w-11', lg: 'h-12 w-12' };

const ICON_CLASSES = { sm: 'w-4 h-4', md: 'w-4 h-4', lg: 'w-5 h-5' };

const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    shape = 'default',
    icon = null,
    iconRight = null,
    loading = false,
    fullWidth = false,
    wrap = false,
    disabled = false,
    to = null,
    href = null,
    type = 'button',
    className = '',
    children,
    onClick,
    ...rest
  },
  ref
) {
  const inactive = disabled || loading;
  const iconOnly = Boolean(icon) && !iconRight && (children == null || children === false || children === '');
  const classes = [
    'inline-flex items-center justify-center font-semibold select-none',
    wrap ? 'whitespace-normal text-left py-2' : 'whitespace-nowrap',
    'transition-colors duration-fast active:scale-[0.98]',
    'focus-visible:outline-none focus-visible:ring-2',
    FLUSH_RING_VARIANTS.has(variant) ? '' : 'focus-visible:ring-offset-2',
    shape === 'pill' ? 'rounded-full' : 'rounded-lg',
    VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary,
    // A wrapping label needs a minimum height, not a fixed one
    iconOnly
      ? (ICON_ONLY_SIZE_CLASSES[size] || ICON_ONLY_SIZE_CLASSES.md)
      : wrap
        ? (WRAP_SIZE_CLASSES[size] || WRAP_SIZE_CLASSES.md)
        : (SIZE_CLASSES[size] || SIZE_CLASSES.md),
    fullWidth ? 'w-full' : '',
    inactive ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
    className,
  ].filter(Boolean).join(' ');

  const iconClass = ICON_CLASSES[size] || ICON_CLASSES.md;
  const spinnerVariant = variant === 'primary' || variant === 'danger' || variant === 'photoGlass' ? 'inverse' : 'primary';
  const content = (
    <>
      {loading
        ? <Spinner size="xs" variant={spinnerVariant} />
        : icon && <Icon name={icon} className={`${iconClass} flex-shrink-0`} />}
      {children}
      {iconRight && !loading && <Icon name={iconRight} className={`${iconClass} flex-shrink-0`} />}
    </>
  );

  // A disabled link has no native disabled state: drop it from the tab order
  // and announce it, rather than leaving a focusable dead link.
  const linkInactiveProps = inactive ? { 'aria-disabled': true, tabIndex: -1 } : {};

  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} aria-busy={loading || undefined} onClick={onClick} {...linkInactiveProps} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a ref={ref} href={href} className={classes} aria-busy={loading || undefined} onClick={onClick} {...linkInactiveProps} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type={type}
      disabled={inactive}
      aria-busy={loading || undefined}
      className={classes}
      onClick={onClick}
      {...rest}
    >
      {content}
    </button>
  );
});

export default Button;
